import {
  useComputedColorScheme,
  Flex,
  Badge,
  Group,
  Text,
  Tooltip,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import {
  getProviderStatusDisplay,
  getProviderStatusPresentation,
} from 'utils/providerClaimStatus';
import type { EOB } from 'interfaces';
import type { MRT_ColumnDef } from 'mantine-react-table';
import { toTitleCase } from 'utils/stringUtils';
import { ICD9Badge } from 'components/common';
import { TariffBadge } from 'components/common/TariffBadge';
import { EOBCard, EOBStack } from 'components/common/EOBStack';
import type { ClaimPatientHistoryRow } from 'pages/claims/components/PatientHistory';
import { formatDateTime, nowISO, isValidDateValue } from 'utils/dateUtils';

export const PatientClaimHistoryColumns = () => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('dark');

  const columns: MRT_ColumnDef<ClaimPatientHistoryRow>[] = [
    {
      accessorKey: 'serviceStart',
      header: 'Service Start',
      accessorFn: (row) => {
        return isValidDateValue(row.serviceStart)
          ? formatDateTime(row.serviceStart)
          : formatDateTime(nowISO());
      },
      Cell: ({ cell }) => {
        return (
          <Text style={{ textAlign: 'center' }}>
            {cell.getValue() as string}
          </Text>
        );
      },
    },
    {
      accessorKey: 'claimNumber',
      header: 'Claim Number',
    },
    {
      accessorKey: 'providerID',
      header: 'Provider',
      accessorFn: (row) =>
        row.provider
          ? row.provider?.title +
            ' ' +
            toTitleCase(row.provider?.firstName, row.provider?.lastName)
          : '',
      enableColumnFilter: true,
      enableGlobalFilter: true,
    },
    {
      accessorKey: 'tariffs',
      header: 'Tariff',
      Cell: ({ row }) => {
        const tariff = row.original?.tariff;
        const hasTariff = Boolean(tariff?.id || tariff?.code);
        return !hasTariff ? (
          <Text>No Tariff(s) Available</Text>
        ) : (
          <Flex direction="column" gap={'md'} w={'100%'} wrap={'nowrap'}>
            <TariffBadge
              key={`${row.id}-${tariff.id}`}
              code={tariff?.code}
              prefix={tariff?.prefix}
              type={tariff?.type}
            />
          </Flex>
        );
      },
    },
    {
      accessorKey: 'tariffs.icd9',
      header: 'ICD9',
      accessorFn: (row) => row.tariff?.icd9 || '',
      Cell: ({ row }) => {
        const icd9 = row.original.tariff?.icd9 || '';
        return icd9 ? <ICD9Badge icd9={icd9} /> : null;
      },
    },
    {
      accessorKey: 'debits.requestedAmount',
      header: 'Billed',
      accessorFn: (row) => {
        return Number(row.debit?.requestedAmount || 0);
      },
      Cell: ({ cell }) => {
        const value = cell.getValue<number>();
        return <Text>{Number(value).toFixed(2)}</Text>;
      },
    },
    {
      accessorKey: 'debits.paidAmount',
      header: 'Amount Paid',
      accessorFn: (row) => {
        return Number(row.debit?.paidAmount || 0);
      },
      Cell: ({ row, cell }) => {
        if (row.original.debit.status === 'pending') {
          return <Text>0</Text>;
        }
        const value = cell.getValue<number>();
        return <Text>{Number(value).toFixed(2)}</Text>;
      },
    },
    {
      accessorKey: 'debits.eobs',
      header: 'EOBs',
      Edit: ({ row }) => {
        const eobs: Array<EOB & { tariffID: string }> = [];
        row.original.debit?.eobs?.forEach((eob: EOB) => {
          if (eob) {
            eobs.push({ ...eob, tariffID: row.original.debit.tariffID });
          }
        });
        return eobs.map((eob) => (
          <Group key={eob.id} gap="xl">
            <EOBCard eob={eob} opened={true} />
          </Group>
        ));
      },
      Cell: ({ row }) => {
        const eobsMap = new Map<string, EOB & { tariffID: string }>();
        row.original.debit?.eobs?.forEach((eob: EOB) => {
          if (eob && !eobsMap.has(eob.code)) {
            eobsMap.set(eob.code, {
              ...eob,
              tariffID: row.original.debit.tariffID,
            });
          }
        });
        const eobs = Array.from(eobsMap.values());
        return <EOBStack eobs={eobs} />;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      accessorFn: (row) =>
        getProviderStatusDisplay(row.tariff?.status?.trim() || row.status),
      Cell: ({ row }) => {
        const status =
          row.original.tariff?.status?.trim() || row.original.status;
        const presentation = getProviderStatusPresentation(
          status,
          computedColorScheme
        );
        if (!presentation.label) return null;

        const tooltip =
          status === CLAIM_TARIFF_STATUS.UNCLAIMABLE
            ? row.original.tariff?.latestComment?.trim() ||
              translate(
                'pages:claims.unclaimableCommentFallback',
                'This tariff was rejected and correctly assessed.'
              )
            : presentation.tooltip;
        const badge = (
          <Badge
            variant="light"
            size="sm"
            radius="xl"
            className="claims-status-badge"
            style={{
              backgroundColor: presentation.backgroundColor,
              border: `1px solid ${presentation.borderColor}`,
              color: presentation.textColor,
            }}
          >
            <Group gap={4} wrap="nowrap">
              <span>{presentation.label}</span>
              {tooltip && (
                <IconInfoCircle
                  size={12}
                  stroke={2}
                  role="img"
                  aria-label={translate(
                    'pages:claims.statusInformation',
                    'Status information'
                  )}
                />
              )}
            </Group>
          </Badge>
        );
        if (!tooltip) return badge;
        return (
          <Tooltip label={tooltip} withArrow multiline w={220} position="top">
            {badge}
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'payorID',
      header: 'Payor',
      accessorFn: (row) => row.payor?.name,
    },
  ];

  return columns;
};
